import React, { useCallback, useEffect, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import {
  TaskyAlert,
  TaskyBadge,
  TaskyButton,
  TaskySelect,
  TaskySpinner,
  TaskyTable,
  TaskyTableBody,
  TaskyTableCell,
  TaskyTableHead,
  TaskyTableHeaderCell,
  TaskyTableRow,
} from '../tasky';
import adminPartnerPortalService, {
  CommissionCell,
  CommissionReport,
  CommissionSection,
} from '../../services/api/adminPartnerPortalService';

type Axis = 'partner' | 'client';

const MONTH_OPTIONS = [6, 12, 24];

const yen = (value: number): string => `¥${Math.round(value).toLocaleString('ja-JP')}`;

const monthLabel = (period: string): string => {
  const [year, month] = period.split('-');
  return `${year}/${month}`;
};

const extractErrorMessage = (error: any, fallback: string): string =>
  error?.response?.data?.error?.message || error?.message || fallback;

/** 金額セル。0円と未計上（請求対象外）は別物なので区別して出す。 */
const AmountCell: React.FC<{ cell?: CommissionCell }> = ({ cell }) => {
  if (!cell) {
    return <span className="text-gray-300">—</span>;
  }
  return (
    <span className={cell.estimated ? 'text-gray-500' : 'text-dark'}>
      {yen(cell.amount)}
      {typeof cell.clientCount === 'number' && (
        <span className="ml-1 text-xs text-gray-400">({cell.clientCount})</span>
      )}
    </span>
  );
};

interface SectionTableProps {
  section: CommissionSection;
  months: string[];
  axis: Axis;
}

const SectionTable: React.FC<SectionTableProps> = ({ section, months, axis }) => {
  const rows =
    axis === 'partner'
      ? section.byPartner.map(row => ({
          key: row.partnerId,
          title: row.partnerName ?? row.partnerCode ?? row.partnerId,
          sub: row.partnerName ? row.partnerCode : null,
          monthly: row.monthly,
          total: row.total,
        }))
      : section.byClient.map(row => ({
          key: `${row.partnerId}-${row.clientId}`,
          title: row.companyName,
          sub: row.partnerName ?? row.partnerCode,
          monthly: row.monthly,
          total: row.total,
        }));

  return (
    <TaskyTable className="min-w-[880px]">
      <TaskyTableHead>
        <TaskyTableRow hoverable={false}>
          <TaskyTableHeaderCell className="whitespace-nowrap">
            {axis === 'partner' ? 'パートナー企業' : '顧客'}
          </TaskyTableHeaderCell>
          {months.map(month => (
            <TaskyTableHeaderCell key={month} className="whitespace-nowrap text-right">
              {monthLabel(month)}
              {section.estimatedMonths.includes(month) && (
                <span className="ml-1 text-xs font-normal text-gray-400">見込</span>
              )}
            </TaskyTableHeaderCell>
          ))}
          <TaskyTableHeaderCell className="whitespace-nowrap text-right">合計</TaskyTableHeaderCell>
        </TaskyTableRow>
      </TaskyTableHead>
      <TaskyTableBody>
        {rows.length === 0 ? (
          <TaskyTableRow hoverable={false}>
            <TaskyTableCell colSpan={months.length + 2} className="py-8 text-center text-gray-500">
              対象期間に手数料の計上がありません
            </TaskyTableCell>
          </TaskyTableRow>
        ) : (
          rows.map(row => (
            <TaskyTableRow key={row.key}>
              <TaskyTableCell className="whitespace-nowrap font-medium text-dark">
                {row.title}
                {row.sub && <div className="text-xs text-gray-400">{row.sub}</div>}
              </TaskyTableCell>
              {months.map(month => (
                <TaskyTableCell key={month} className="whitespace-nowrap text-right">
                  <AmountCell cell={row.monthly[month]} />
                </TaskyTableCell>
              ))}
              <TaskyTableCell className="whitespace-nowrap text-right font-semibold text-dark">
                {yen(row.total)}
              </TaskyTableCell>
            </TaskyTableRow>
          ))
        )}
        <TaskyTableRow hoverable={false}>
          <TaskyTableCell className="whitespace-nowrap font-semibold text-dark">合計</TaskyTableCell>
          {months.map(month => (
            <TaskyTableCell key={month} className="whitespace-nowrap text-right font-semibold">
              <AmountCell cell={section.totals[month]} />
            </TaskyTableCell>
          ))}
          <TaskyTableCell className="whitespace-nowrap text-right font-semibold text-dark">
            {yen(section.grandTotal)}
          </TaskyTableCell>
        </TaskyTableRow>
      </TaskyTableBody>
    </TaskyTable>
  );
};

/**
 * 手数料売上の月次レポート。
 *
 * 確定分はポータルの請求書（単価・レートのスナップショット）、当月は同じ計算式で立てた
 * 見込み。計上対象は有料契約中の顧客だけなので、パートナー経由の組織数とは一致しない。
 */
const PartnerCommissionReport: React.FC = () => {
  const [report, setReport] = useState<CommissionReport | null>(null);
  const [months, setMonths] = useState(12);
  const [axis, setAxis] = useState<Axis>('partner');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      setReport(await adminPartnerPortalService.getCommissions({ months }));
    } catch (err: any) {
      console.error('Failed to load partner commissions:', err);
      // 金額なので、取得失敗を 0 円として見せない。
      setReport(null);
      setError(extractErrorMessage(err, '手数料の集計を取得できませんでした'));
    } finally {
      setIsLoading(false);
    }
  }, [months]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <section>
      <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="text-base font-semibold text-dark">手数料売上</h2>
          <p className="mt-1 text-xs text-gray-500">
            確定分はパートナーポータルの請求書、当月は同じ計算式で立てた見込みです。
            <span className="font-medium text-dark">計上対象は有料契約中の顧客のみ</span>
            のため、パートナー経由の組織数とは一致しません。
          </p>
        </div>
        <div className="flex items-center gap-2">
          <TaskySelect
            value={String(months)}
            onChange={event => setMonths(Number(event.target.value))}
            className="w-32"
          >
            {MONTH_OPTIONS.map(option => (
              <option key={option} value={option}>
                直近{option}ヶ月
              </option>
            ))}
          </TaskySelect>
          <TaskyButton size="sm" variant="secondary" onClick={load} disabled={isLoading}>
            <RefreshCw />
            再読み込み
          </TaskyButton>
        </div>
      </div>

      <div className="mb-4 inline-flex rounded-tasky-md border border-warm-border p-0.5">
        {(['partner', 'client'] as Axis[]).map(value => (
          <button
            key={value}
            type="button"
            onClick={() => setAxis(value)}
            className={`px-3 py-1.5 text-sm font-medium rounded-tasky-sm ${
              axis === value ? 'bg-primary text-white' : 'text-gray-600 hover:text-dark'
            }`}
          >
            {value === 'partner' ? '代理店ごと' : '顧客ごと'}
          </button>
        ))}
      </div>

      {error && (
        <TaskyAlert tone="error" className="mb-4">
          {error}
        </TaskyAlert>
      )}

      {isLoading && !report ? (
        <div className="flex items-center justify-center gap-3 py-10 text-sm text-gray-500">
          <TaskySpinner size="md" />
          読み込み中...
        </div>
      ) : (
        report && (
          <div className="space-y-6">
            {report.sections.length === 0 ? (
              <p className="py-6 text-center text-sm text-gray-500">対象期間に手数料の計上がありません</p>
            ) : (
              report.sections.map(section => (
                <div key={section.direction}>
                  <div className="mb-2 flex items-center gap-2">
                    <h3 className="text-sm font-semibold text-dark">{section.label}</h3>
                    <TaskyBadge variant="secondary">{yen(section.grandTotal)}</TaskyBadge>
                    {section.estimatedMonths.length > 0 && (
                      <span className="text-xs text-gray-400">
                        {section.estimatedMonths.map(monthLabel).join('・')} は見込み
                      </span>
                    )}
                  </div>
                  <SectionTable section={section} months={report.months} axis={axis} />
                </div>
              ))
            )}
          </div>
        )
      )}
    </section>
  );
};

export default PartnerCommissionReport;
